import debounce from 'lodash/debounce';
import moment from 'moment';
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {Heading, IconButton, SearchInput, SegmentedControl} from '@albert-io/atomic';
import {DiagnosticsGroup} from 'client/AdaptiveDiagnostics/SkillPathsTab/DiagnosticsGroup';
import {useSkillPathsDiagnostics} from 'client/AdaptiveDiagnostics/SkillPathsTab/useSkillPathsDiagnostics';
import {useAdaptiveSubjectsStore} from 'client/AdaptiveSubjects/AdaptiveSubjects.store';
import {buildReportURLFromLevels} from 'client/AdaptiveSubjects/utils/buildReportUrlFromLevels';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';
import {ErrorRetryState} from 'client/TeacherDashboard/components/ErrorRetryState';
import {AdaptiveOnboardingEmptyState} from 'client/TeacherDashboard/components/AdaptiveEmptyState';
import {ListRowsSkeleton} from 'client/TeacherDashboard/components/ListRowsSkeleton';
import {SortDropdown} from 'client/TeacherDashboard/components/SortDropdown';
import type {SortValue} from 'client/TeacherDashboard/types';

import {DEFAULT_SORT_ORDER, SORT_OPTIONS, STATUS_SEGMENTS} from './constants';
import {SkillPathListGroup, SkillPathListGroupEmpty} from './SkillPathListGroup';
import {InfiniteScrollSentinel} from './InfiniteScrollSentinel';
import {SkillPathRow} from './SkillPathRow';
import {SkillPathsViewSkeleton} from './SkillPathsViewSkeleton';
import {
  useClassContentRevealed,
  useReportClassContentReady
} from 'client/TeacherDashboard/ClassDetail/useClassContentReady';
import {SkillPathStatusEmptyLine} from './SkillPathsEmptyState';
import {SkillPathsFiltersMenu} from './SkillPathsFiltersMenu';
import type {
  DueWithinFilter,
  SkillPathRowData,
  SkillPathSortValue,
  SkillPathStatusSegment
} from './types';
import {useSkillPathStatusCounts} from './hooks/useSkillPathStatusCounts';
import {useSkillPathsList} from './hooks/useSkillPathsList';

const SkillPathModalHost = lazy(() => import('./SkillPathModalHost'));

const DEFAULT_SORT: SortValue<SkillPathSortValue> = {field: 'recentlyEdited', order: 'desc'};

const getInitialSegment = (): SkillPathStatusSegment => {
  const hash = window.location.hash.substring(1);
  const match = STATUS_SEGMENTS.find((s) => s.value === hash);
  return match ? match.value : 'all';
};

interface Props {
  classroomId?: string;
}

export const SkillPathsList = ({classroomId}: Props) => {
  // Switching class scope resets filters + sort but keeps the current tab.
  const [segment, setSegment] = useState<SkillPathStatusSegment>(getInitialSegment);

  const handleSegmentChange = (value: string | number) => {
    if (value === segment) {
      return;
    }
    window.location.hash = String(value);
    setSegment(value as SkillPathStatusSegment);
  };

  return (
    <SkillPathsListBody
      key={classroomId ?? 'all'}
      classroomId={classroomId}
      segment={segment}
      onSegmentChange={handleSegmentChange}
    />
  );
};

interface BodyProps {
  classroomId?: string;
  segment: SkillPathStatusSegment;
  onSegmentChange: (value: string | number) => void;
}

const SkillPathsListBody = ({classroomId, segment, onSegmentChange}: BodyProps) => {
  const revealed = useClassContentRevealed();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>('all');
  const [dueWithin, setDueWithin] = useState<DueWithinFilter>('schoolYear');
  const [dueDateStart, setDueDateStart] = useState('');
  const [dueDateEnd, setDueDateEnd] = useState('');
  // What the inputs show, and what the queries are keyed on. A date input reports every edit that
  // leaves a valid date, so typing out a year emits one per digit — and each one is a new set of
  // variables for eight queries, the most expensive of which run for seconds under a date filter.
  const [committedDueDates, setCommittedDueDates] = useState({start: '', end: ''});
  const [sort, setSort] = useState<SortValue<SkillPathSortValue>>(DEFAULT_SORT);

  const debouncedSetSearch = useMemo(() => debounce(setDebouncedSearchTerm, 400), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const debouncedCommitDueDates = useMemo(() => debounce(setCommittedDueDates, 400), []);
  useEffect(() => () => debouncedCommitDueDates.cancel(), [debouncedCommitDueDates]);

  // Opening the shared skill modal: hydrate the adaptive-subjects store's selectedSkill and sync the
  // subject's allowed classrooms; the modal renders off that store state (no guide/levels needed).
  const setSelectedSkill = useAdaptiveSubjectsStore((state) => state.setSelectedSkill);
  const isModalOpen = useAdaptiveSubjectsStore((state) => !!state.selectedSkill);
  const [editingSubjectId, setEditingSubjectId] = useState('');
  const [openInEditMode, setOpenInEditMode] = useState(false);

  // Cross-surface reset: selectedSkill lives in the shared adaptive-subjects store, which the browse
  // page (AdaptiveSubjects) also drives via a dismissable={false} modal — leaving browse via browser
  // Back strands a selectedSkill. A layout effect clears it before paint on mount, so a stranded value
  // can't even flash this surface's modal open, and on unmount so this surface never strands one for
  // another. Mount runs once, before any row click can set selectedSkill, so it can never close a modal
  // the teacher opened on this surface.
  useLayoutEffect(() => {
    setSelectedSkill(null);
    return () => setSelectedSkill(null);
  }, [setSelectedSkill]);

  const sharedFilters = {
    classroomId,
    segment,
    search: debouncedSearchTerm,
    subject,
    dueWithin,
    dueDateStart: committedDueDates.start,
    dueDateEnd: committedDueDates.end,
    sort
  };

  const {rows, subjectOptions, isLoading, hasError, hasNextPage, isLoadingMore, loadMore, refetch} =
    useSkillPathsList(sharedFilters);
  const {
    counts,
    isEmptyInEveryStatus,
    isLoading: isCountsLoading,
    hasError: hasCountsError,
    refetch: refetchCounts
  } = useSkillPathStatusCounts(sharedFilters);

  // Both lists read the same toolbar state. Held here rather than inside the group so the two can
  // resolve into one render — the teacher sees both sections appear together or neither.
  const diagnostics = useSkillPathsDiagnostics(sharedFilters);

  // The totals come from their own query, so a filter change resolves them and the rows at
  // different moments. Holding the rows until both are in stops the list reading "no open skill
  // paths" beneath a tab still showing the previous filter's count.
  const isSettling = isLoading || isCountsLoading || diagnostics.isLoading;

  // Refresh on modal close so edits there land without a reload — but only if it wrote something.
  // Opening a row to read it is the common case, and these two reads are the page's most expensive.
  const wasModalOpen = useRef(false);
  const didModalSave = useRef(false);
  const handleModalSaved = useCallback(() => {
    didModalSave.current = true;
  }, []);
  useEffect(() => {
    if (wasModalOpen.current && !isModalOpen && didModalSave.current) {
      didModalSave.current = false;
      refetch();
      refetchCounts();
    }
    wasModalOpen.current = isModalOpen;
  }, [isModalOpen, refetch, refetchCounts]);

  // One status control drives both groups, so each tab counts the two lists together.
  const diagnosticCounts = diagnostics.counts;
  const hasAllCounts = Boolean(counts) && diagnostics.hasCounts;
  const totals = useMemo(
    () => ({
      all: (counts?.all ?? 0) + diagnosticCounts.all,
      open: (counts?.open ?? 0) + diagnosticCounts.open,
      closed: (counts?.closed ?? 0) + diagnosticCounts.closed,
      scheduled: (counts?.scheduled ?? 0) + diagnosticCounts.scheduled
    }),
    [counts, diagnosticCounts]
  );

  // The two lists count themselves in separate queries, so a live sum briefly shows one filter's
  // number added to the other's. Hold the last complete pair until both have answered.
  const areTotalsSettling = isCountsLoading || diagnostics.isCountsLoading;
  const [shownTotals, setShownTotals] = useState(totals);
  if (!areTotalsSettling && shownTotals !== totals) {
    setShownTotals(totals);
  }

  const segmentOptions = useMemo(
    () =>
      STATUS_SEGMENTS.map((s) => ({
        ...s,
        label: hasAllCounts ? (
          <>
            {s.label} <span className='ml-x0-half content-tertiary'>({shownTotals[s.value]})</span>
          </>
        ) : (
          s.label
        )
      })),
    [shownTotals, hasAllCounts]
  );

  // The totals live in their own query, so a retry has to re-pull both or the tabs stay wrong.
  const retryAll = useCallback(() => {
    refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);

  // A new field starts in its own default direction rather than inheriting the previous one.
  const handleSortChange = (next: SortValue<SkillPathSortValue>) => {
    setSort(
      next.field === sort.field ? next : {field: next.field, order: DEFAULT_SORT_ORDER[next.field]}
    );
  };

  // The input updates on the keystroke; the queries wait for the teacher to stop typing.
  const handleDueDateChange = (bound: 'start' | 'end', value: string) => {
    const next =
      bound === 'start' ? {start: value, end: dueDateEnd} : {start: dueDateStart, end: value};
    if (bound === 'start') {
      setDueDateStart(value);
    } else {
      setDueDateEnd(value);
    }
    debouncedCommitDueDates(next);
  };

  // The bounds survive a detour through a preset: `dueWithinBounds` reads them only in its `custom`
  // branch, so they are inert meanwhile and still there on the way back. Flushed rather than left
  // pending, or the inputs would show a bound the query is not filtering on.
  const handleDueWithinChange = (value: DueWithinFilter) => {
    setDueWithin(value);
    debouncedCommitDueDates.cancel();
    setCommittedDueDates({start: dueDateStart, end: dueDateEnd});
  };

  const clearSearch = () => {
    setSearchTerm('');
    debouncedSetSearch.cancel();
    setDebouncedSearchTerm('');
  };

  const openModal = useCallback(
    (row: SkillPathRowData, editMode: boolean) => {
      setOpenInEditMode(editMode);
      setEditingSubjectId(row.subjectId);
      setSelectedSkill({
        id: row.id,
        name: row.skillName,
        code: row.code,
        themeId: row.themeId,
        topicId: row.topicId,
        subtopicId: row.id,
        subjectId: row.subjectId,
        guideId: row.guideId,
        onStudentsSkillPaths: row.onStudentsSkillPaths
      });
    },
    [setSelectedSkill]
  );
  const handleOpen = useCallback((row: SkillPathRowData) => openModal(row, false), [openModal]);
  const handleEdit = useCallback((row: SkillPathRowData) => openModal(row, true), [openModal]);

  // Opens the teacher report drilled into this skill (subject → guide → theme → topic → subtopic) on
  // the students dimension.
  const handleViewReport = useCallback(
    (row: SkillPathRowData) => {
      const reportUrl = buildReportURLFromLevels(
        [row.subjectId, row.guideId, row.themeId, row.topicId, row.id],
        // Read at click time, so no bridge is needed: this runs outside render.
        masqueradeStore.getUserIdByMasqueradeState(),
        classroomId
      );
      // Local day, like the date the row displays and like the report's own filter — converting
      // in UTC would land the teacher a day off from the row they clicked.
      const startDate = row.minStartDate ? moment(row.minStartDate).format('YYYY-MM-DD') : null;
      history.push(`${reportUrl}/students${startDate ? `?startDate=${startDate}` : ''}`);
    },
    [classroomId]
  );

  const skillPathRowList = (
    <>
      <ul className='m-x0 p-x0 flex list-none flex-col'>
        {rows.map((row) => (
          <li key={row.id}>
            <SkillPathRow
              row={row}
              onOpen={handleOpen}
              onEdit={handleEdit}
              onReport={handleViewReport}
            />
          </li>
        ))}
      </ul>
      <InfiniteScrollSentinel
        hasNextPage={hasNextPage}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      />
    </>
  );

  const groupedSkillPathRows = hasError ? (
    <ErrorRetryState copy='We couldn’t load your skill paths.' onRetry={retryAll} />
  ) : rows.length > 0 ? (
    skillPathRowList
  ) : (
    <SkillPathListGroupEmpty inset={isEmptyInEveryStatus ? 'panel' : 'text'}>
      {isEmptyInEveryStatus ? (
        <AdaptiveOnboardingEmptyState
          classroomId={classroomId}
          layout='inline'
          variant='skillPaths'
        />
      ) : (
        <SkillPathStatusEmptyLine segment={segment} />
      )}
    </SkillPathListGroupEmpty>
  );

  // Nothing in either list under these filters: one pitch covering both, and no group headings —
  // there are no two groups to tell apart yet. Failures outrank it, or collapsing to the pitch would
  // hide both groups' error panels and with them the only Try Again the teacher has.
  const isEmptyEverywhere =
    !hasError && !diagnostics.hasError && isEmptyInEveryStatus && diagnostics.isEmptyInEveryStatus;

  // Sticky: the whole-view skeleton is for first paint only. Later loads keep the toolbar on screen
  // so the teacher can still switch tabs while rows are fetching.
  const [hasRevealed, setHasRevealed] = useState(false);
  if (!hasRevealed && (hasAllCounts || hasError || hasCountsError || diagnostics.hasError)) {
    setHasRevealed(true);
  }

  useReportClassContentReady(hasRevealed);

  if (!hasRevealed || !revealed) {
    return <SkillPathsViewSkeleton />;
  }

  return (
    <>
      <div className='gap-x4 pt-x4 flex flex-col'>
        <Heading as='h2' className='m-x0' size='s'>
          Skill Paths
        </Heading>
        <div className='gap-x2 flex flex-wrap items-center justify-between'>
          <SegmentedControl
            buttonClassName='font-normal'
            onSelect={onSegmentChange}
            options={segmentOptions}
            selectedIdx={STATUS_SEGMENTS.findIndex((s) => s.value === segment)}
          />
          <div className='gap-x1 flex items-center'>
            <SkillPathsFiltersMenu
              dueWithin={dueWithin}
              dueDateStart={dueDateStart}
              dueDateEnd={dueDateEnd}
              subject={subject}
              subjectOptions={subjectOptions}
              onDueWithinChange={handleDueWithinChange}
              onDueDateChange={handleDueDateChange}
              onSubjectChange={setSubject}
            />
            <SortDropdown
              options={SORT_OPTIONS}
              triggerIcon={['far', 'arrow-down-short-wide']}
              triggerLabel='Sort'
              value={sort}
              onChange={handleSortChange}
            />
            {isSearchOpen ? (
              <div
                onBlur={(e) => {
                  if (!searchTerm && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    setIsSearchOpen(false);
                  }
                }}
              >
                <SearchInput
                  autoFocus
                  className='w-[256px]'
                  placeholder='Search'
                  rectangle
                  value={searchTerm}
                  onChange={(_e, value) => {
                    setSearchTerm(value);
                    debouncedSetSearch(value);
                  }}
                  onReset={() => {
                    clearSearch();
                    setIsSearchOpen(false);
                  }}
                />
              </div>
            ) : (
              <IconButton
                color='secondary'
                icon='magnifying-glass'
                label='Search classwork'
                roundness='square'
                variant='outlined'
                onClick={() => setIsSearchOpen(true)}
              />
            )}
          </div>
        </div>
        {/* A count only describes a list that rendered; over an error panel it contradicts it. */}
        {!hasError && !diagnostics.hasError && (
          <div aria-live='polite' className='sr-only'>
            {`Showing ${diagnostics.diagnostics.length} ${
              diagnostics.diagnostics.length === 1 ? 'diagnostic' : 'diagnostics'
            } and ${rows.length} ${rows.length === 1 ? 'skill path' : 'skill paths'}`}
          </div>
        )}
        {isSettling ? (
          <ListRowsSkeleton label='Loading diagnostics and skill paths…' />
        ) : isEmptyEverywhere ? (
          <SkillPathListGroupEmpty inset='panel'>
            <AdaptiveOnboardingEmptyState
              classroomId={classroomId}
              layout='inline'
              variant='both'
            />
          </SkillPathListGroupEmpty>
        ) : (
          <>
            <DiagnosticsGroup
              classroomId={classroomId}
              diagnostics={diagnostics.diagnostics}
              hasError={diagnostics.hasError}
              hasMore={diagnostics.hasMore}
              isEmptyInEveryStatus={diagnostics.isEmptyInEveryStatus}
              isLoadingMore={diagnostics.isLoadingMore}
              segment={segment}
              total={diagnostics.total}
              onDeleted={diagnostics.refetch}
              onRetry={diagnostics.refetch}
              onShowMore={diagnostics.showMore}
            />
            <SkillPathListGroup
              // On a counts failure the fallback would be the previous filter's number — worse
              // than none against this filter's rows.
              count={hasCountsError ? undefined : counts?.[segment]}
              title='Skill Practice'
            >
              {groupedSkillPathRows}
            </SkillPathListGroup>
          </>
        )}
      </div>
      {isModalOpen && (
        <Suspense fallback={null}>
          <SkillPathModalHost
            initialEditMode={openInEditMode}
            subjectId={editingSubjectId}
            onSaved={handleModalSaved}
          />
        </Suspense>
      )}
    </>
  );
};
